import {
  Divider,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Done } from "@mui/icons-material";
import { GridApi } from "ag-grid-community";
import GridEventProvider from "components/context/GridEventContext";
import MButton from "components/momentum/MButton";
import { FEATURE_KEY, ROLE } from "constant";
import { useAxios } from "myAxios";
import { AuthProvider } from "pages";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Account } from "types/portfolio";
import { checkSelectAll, trackButton } from "utils";
import { GirdViewFunction } from "../GridViewFunctionBar";
import BulkExceptionDialog from "../NewEM/BulkExceptionDialog";

interface PostParams {
  reason: string | null;
  accountKeys: string[];
  coverageStatus?: string;
  engagementValue?: string;
  wemField?: string;
}

const useStyles = makeStyles(() => ({
  common: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 4px",
    borderRadius: 2,
    fontFamily: "CiscoSansTT",
    fontSize: 9,
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "14px",
    marginLeft: 8,
  },
  auto: {
    background: "#DEDEDE",
    color: "#252B34",
  },
  manual: {
    background: "#C3E9FE",
    color: "#0A78CC",
  },
  MuiListItemIcon: {
    minWidth: 20,
  },
  MuiListItemTextPrimary: {
    color: "#333",
    fontFamily: "CiscoSansTT",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "12px",
  },
  clearText: {
    color: "#333",
    fontFamily: "CiscoSansTT",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "12px",
  },
  changeText: {
    color: "#777",
    fontFamily: "CiscoSansTT",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "18px",
    pointerEvents: "none",
  },
  delText: {
    color: "#aaa",
  },
  optionWrapper: {
    display: "flex",
    alignItems: "center",
  },
  option: {
    display: "flex",
    alignItem: "center",
    justifyContent: "space-between",
    "&:hover": {
      backgroundColor: "#EBFAFF",
    },
    "&:focus": {
      backgroundColor: "#EBFAFF",
    },
    "&:active": {
      backgroundColor: "#EBFAFF",
    },
    "&:select": {
      backgroundColor: "#EBFAFF",
    },
  },
  leftWrapper: {
    display: "flex",
  },
}));

export const menuConfig = [
  {
    label: "High Touch",
    color: "#00823B",
    // mark: "auto",
  },
  {
    label: "Scale",
    color: "#FF9A24",
    // mark: "manual",
  },
  {
    label: "Fed Longtail",
    color: "#6C9EF5",
    // mark: "manual",
  },
  /* {
    label: "Care Agent Proactive",
    color: "#C299FF",
    // mark: "manual",
  },
  {
    label: "Care Agent Reactive",
    color: "#00BAD4",
    // mark: "manual",
  }, */
];

// for Non-FedRamp
export const nonFedMenuConfig = [
  {
    label: "High Touch",
    color: "#00823B",
    // mark: "auto",
  },
  {
    label: "Scale",
    color: "#FF9A24",
    // mark: "manual",
  },
  /* {
    label: "Care Agent Proactive",
    color: "#C299FF",
    // mark: "manual",
  }, */
  {
    label: "TAC Supported",
    color: "#00BAD4",
    // mark: "manual",
  },
];

export const fullMenuConfig = [
  {
    label: "High Touch",
    color: "#00823B",
    priority: 1,
    condition: () => true,
    // mark: "auto",
  },
  {
    label: "Scale",
    color: "#FF9A24",
    priority: 2,
    condition: () => true,
    // mark: "manual",
  },
  {
    label: "Fed Longtail",
    color: "#6C9EF5",
    priority: 3,
    condition: (isCGEM: boolean, _: boolean, isFed: boolean) => isFed,
  },
  {
    label: "TAC Supported",
    color: "#00BAD4",
    priority: 4,
    condition: (isCGEM: boolean, isAmericas: boolean, isFed: boolean) => (isCGEM ? false : !isFed),
    // mark: "manual",
  },
];

const CoverageStatus: FC<
  GirdViewFunction & {
    transformer: () => Promise<{ data: any }>;
  }
> = ({ anchorEl, open, onClick, ready, onClose, selectedRows, transformer }) => {
  const classes = useStyles();
  const axios = useAxios();
  const authDetail = useContext(AuthProvider);
  const [totalCount, setTotalCount] = useState(0);

  const { gridEvent } = useContext(GridEventProvider);
  const epCellRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [selected, setSelected] = useState("");
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [accountKeys, setAccountKeys] = useState<string[]>([]);

  useEffect(() => {
    const dataLoadedHandler = (e: any) => {
      const { totalCount: total } = e.detail;
      setTotalCount(total);
    };
    addEventListener("DATA_LOADED", dataLoadedHandler);
    return () => {
      removeEventListener("DATA_LOADED", dataLoadedHandler);
    };
  }, []);

  const handleClose = () => {
    onClose();
  };

  const handleExpClose = () => {
    setExceptionOpen(false);
  };

  const clearException = async () => {
    trackButton(`CoverageStatus_Set as ${selected ?? "High Touch"}`);
    const selectAll = checkSelectAll();
    const selectedRows: Account[] | undefined = gridEvent?.api.getSelectedRows();
    if (!selectedRows?.length) {
      toast.error("Please select an account first!");
      return;
    }
    if ((selectAll && totalCount > 1000) || selectedRows.length > 1000) {
      toast.error("Maximum limit - 1000 Accounts");
      return;
    }
    let accountKeys: string[] = [];
    let toastId: React.ReactText = "";

    if (selectAll) {
      toastId = toast.info(
        <>
          <p>Operating bulk action, this might take a while</p>
          <LinearProgress
            color="primary"
            style={{ borderRadius: 5 }}
          />
        </>,
        {
          position: "top-right",
          autoClose: false,
        }
      );
      try {
        const { data } = await transformer();
        accountKeys = Array.from(
          new Set(
            data
              .filter(
                (x: any) =>
                  authDetail.features.includes(FEATURE_KEY.UPDATE_ACCOUNT_COVERAGE) ||
                  (authDetail.role === ROLE.DEV_ROLE && x.accountType !== "Online")
              )
              .map((x: any) => x.accountKey)
          )
        );
      } catch {
        toast.error("Failed to get accounts");
        toast.dismiss(toastId);
      }
    } else {
      accountKeys = Array.from(
        new Set(
          selectedRows
            .filter(
              (x) =>
                authDetail.features.includes(FEATURE_KEY.UPDATE_ACCOUNT_COVERAGE) ||
                (authDetail.role === ROLE.DEV_ROLE && x.accountType !== "Online")
            )
            .map((x) => x.accountKey)
        )
      );
    }

    const postData = {
      coverageStatus: selected ?? "High Touch",
      accountKeys,
      reason: null,
    };

    axios
      .put(`/api/dataservice/v1/accounts/account-engagement-model`, postData)
      .then((res) => {
        // console.log(res.data);
        const result = res.data;
        gridEvent?.api.forEachNode((n: any) => {
          if (accountKeys.includes(n.data.accountKey)) {
            n.setDataValue("coverageStatus", result?.[n.data.accountKey]);
            n.setDataValue("aemExpReason", null);
          }
        });
        gridEvent?.api.redrawRows();
        onClose();
        toast.dismiss(toastId);
      })
      .catch(() => {
        toast.dismiss(toastId);
        toast.error(`Fail to set account(s) as ${selected ?? "High Touch"}`);
      });
  };

  const handleChange = (item: string) => async () => {
    const selectAll = checkSelectAll();
    const selectedRows: Account[] | undefined = gridEvent?.api.getSelectedRows();
    if ((selectAll && totalCount > 1000) || (selectedRows && selectedRows.length > 1000)) {
      toast.error("Maximum limit - 1000 Accounts");
      return;
    }
    let accountKeyList: string[] = [];
    let toastId: React.ReactText = "";

    if (selectAll) {
      toastId = toast.info(
        <>
          <p>Operating bulk action, this might take a while</p>
          <LinearProgress
            color="primary"
            style={{ borderRadius: 5 }}
          />
        </>,
        {
          position: "top-right",
          autoClose: false,
        }
      );
      try {
        const { data } = await transformer();
        accountKeyList = Array.from(
          new Set(
            data
              .filter(
                (x: any) =>
                  authDetail.features.includes(FEATURE_KEY.UPDATE_ACCOUNT_COVERAGE) ||
                  (authDetail.role === ROLE.DEV_ROLE && x.accountType !== "Online")
              )
              .map((x: any) => x.accountKey)
          )
        );
      } catch {
        toast.error("Failed to get accounts");
        toast.dismiss(toastId);
      }
    } else {
      if (selectedRows && selectedRows?.length) {
        accountKeyList = Array.from(
          new Set(
            selectedRows
              .filter(
                (x) =>
                  authDetail.features.includes(FEATURE_KEY.UPDATE_ACCOUNT_COVERAGE) ||
                  (authDetail.role === ROLE.DEV_ROLE && x.accountType !== "Online")
              )
              .map((x) => x.accountKey)
          )
        );
      }
    }
    setAccountKeys(accountKeyList);
    setSelected(item);
    setExceptionOpen(true);
    handleClose();
  };

  const selectCallback = (data: string) => {
    setSelected(data);
  };
  return (
    <>
      <div ref={epCellRef}>
        <Tooltip
          disableHoverListener={!!!selectedRows}
          disableFocusListener={!!!selectedRows}
          disableTouchListener={!!!selectedRows}
          open={showTooltip}
          arrow={true}
          title={
            <div>
              <b>Disabled until account(s) selected</b>
            </div>
          }
        >
          <span
            onMouseOverCapture={!selectedRows ? () => setShowTooltip(true) : undefined}
            onMouseOutCapture={!selectedRows ? () => setShowTooltip(false) : undefined}
            style={{ marginRight: 5 }}
          >
            <MButton
              onClick={onClick}
              disabled={!ready || !selectedRows}
            >
              Account Engagement Model
            </MButton>
          </span>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={clearException}>
            <ListItemIcon classes={{ root: classes.MuiListItemIcon }}>
              <img
                src="/portfolio/trash.svg"
                style={{
                  marginLeft: -2,
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Clear Manual Exception"
              className={classes.clearText}
            />
          </MenuItem>
          <Divider style={{ height: 1 }} />
          <MenuItem className={classes.changeText}>
            <ListItemText primary="Change Engagement Model" />
          </MenuItem>
          {fullMenuConfig.map((item) => (
            <MenuItem
              key={item.label}
              value={item.label}
              onClick={handleChange(item.label)}
              className={classes.option}
            >
              <div className={classes.leftWrapper}>
                {/* <ListItemIcon classes={{ root: classes.MuiListItemIcon }}>
                <img src={item.iconSrc} width={12} height={10} />
              </ListItemIcon> */}
                <div className={classes.optionWrapper}>
                  <ListItemText
                    primary={item.label}
                    style={{ color: item.color }}
                  />
                  {/* <div
                  className={`${classes.common} ${
                    item.mark === "auto" ? classes.auto : classes.manual
                  }`}
                  style={{ textTransform: "capitalize" }}
                >
                  {item.mark}
                </div> */}
                </div>
              </div>
              {selected === item.label ? (
                <Done
                  htmlColor="#00A0D1"
                  fontSize="small"
                />
              ) : null}
            </MenuItem>
          ))}
        </Menu>
      </div>
      <BulkExceptionDialog
        selected={selected}
        gridEvent={gridEvent}
        open={exceptionOpen}
        onClose={handleExpClose}
        accountKeyArr={accountKeys}
        selectedCallback={selectCallback}
      />
    </>
  );
};

export const favoriteCallback = (api?: GridApi) => {
  let active = false;
  const filterModel = api?.getFilterModel();
  if (filterModel?.externalFilter?.filter === "Favorites") {
    active = true;
  }
  if (active) {
    api?.setFilterModel(api?.getFilterModel());
  }
};

export default CoverageStatus;
